import { getPaginatedEmployeesWithDelegates } from "@api";
import {
  EmployeeDelegateItem,
  EmployeeWithDelegates,
} from "@api/types/employeeswithdelegates";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography,
} from "@material-ui/core";
import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import DataTable from "react-data-table-component";
import { MaintenanceBreadcrumbs } from "../breadcrumbs";
import { M_TABLE_HEADERS_EMPLOYEEDELEGATES } from "../constants";
import { EmployeeWithDelegatesDetails } from "./detail/detail";
import { Form } from "@components";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import { Actions } from "../../../../../screens/request/components/actions";
import { Search } from "@components/table/toolbar/search-filters";
import CloseIcon from "@material-ui/icons/Close";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import { postEmployeeDelegate } from "@api/admin/employeedelegates";
import { alertService } from "@services";
import Hooks from "@hooks";
import { exportEmployeeDelegates } from "@api/excel-exporters/exportEmployeeDelegates";

const columns = [
  {
    name: M_TABLE_HEADERS_EMPLOYEEDELEGATES.EMAIL,
    selector: "userEmail",
  },
  {
    name: M_TABLE_HEADERS_EMPLOYEEDELEGATES.NAME,
    selector: "name",
  },
];

type EmployeesWithDelegatesPaginated = {
  employeesWithDelegates: EmployeeWithDelegates[];
  totalCount: number;
};

type Key = keyof EmployeeDelegateItem;

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

export const EmployeeDelegates: React.FC = () => {
  const addForm = Form.useManager<EmployeeDelegateItem>({
    id: 0,
    userEmail: "",
    delegateEmail: "",
    isActive: false,
  });
  const [employees, setEmployees] = useState<EmployeeWithDelegates[]>([]);
  const [isLoading, setIsloading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [where, setWhere] = useState<string>("");
  // const [selectedItem, setSelectedItem] = useState<
  //   EmployeeWithDelegates | undefined
  // >(undefined);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<
    number | undefined
  >(undefined);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openAddForm, setOpenAddForm] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [
    directory_userEmails,
    ,
    directory_userEmails_fetching,
  ] = Hooks.useDirectory(addForm.values.userEmail);
  const [
    directory_delegateEmails,
    ,
    directory_delegateEmails_fetching,
  ] = Hooks.useDirectory(addForm.values.delegateEmail);

  useEffect(() => {
    getDataList();
  }, [page, where]);

  function getDataList() {
    getPaginatedEmployeesWithDelegates(page, perPage, where).then(
      (response: EmployeesWithDelegatesPaginated) => {
        setEmployees(response.employeesWithDelegates);
        setTotal(response.totalCount);
        setIsloading(false);
      }
    );
  }

  const onSearchUpdate = useCallback((term: string) => {
    setWhere(term);
  }, []);

  //useEffect(() => {}, [openModal]);

  function resetAddForm() {
    addForm.values.id = 0;
    addForm.values.userEmail = "";
    addForm.values.delegateEmail = "";
    addForm.values.isActive = false;
  }

  function handleCloseModal() {
    setSelectedEmployeeId(undefined);
    setOpenModal(false);
  }

  function closeAddForm() {
    resetAddForm();
    setOpenAddForm(false);
  }

  let DelegateItem: EmployeeDelegateItem = {
    id: 0,
    userEmail: "",
    delegateEmail: "",
    isActive: false,
  };

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "userEmail":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) || Form.Validators.isEmail(value),
        ]);
      case "delegateEmail":
        return Promise.resolve([
          name as Key,
          Form.Validators.hasValue(value) || Form.Validators.isEmail(value),
        ]);
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function handleAddFormChange(name: Key, value: any): void {
    addForm.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      addForm.set(Form.target.ERROR, [name, error]);
    });
  }

  const handleAddFormSubmit = useCallback(() => {
    Promise.all(Object.entries(addForm.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          sendPayload();
        } else {
          validations.forEach(([name, error]) => {
            addForm.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [addForm]);

  function sendPayload() {
    setIsSubmitting(true);
    DelegateItem.id = 0;
    DelegateItem.userEmail = addForm.values.userEmail;
    DelegateItem.delegateEmail = addForm.values.delegateEmail;
    DelegateItem.isActive = addForm.values.isActive;
    postEmployeeDelegate(DelegateItem.id, DelegateItem)
      .then((response) => {
        if (response) {
          alertService.success({ msg: "Delegate successfully added." });
          // if (response.countBeforeAdd === 0) {
          //   //console.log("new entry");
          //   getDataList();
          // }
        }
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({
            msg:
              "Please check if delegate already exists for this user and try again.",
          });
        }
      })
      .then(() => {
        closeAddForm();
        setIsSubmitting(false);
      });
  }

  // since we are sure that we will always find an existing EmployeeWithDelegates
  // entity from the table, we cast the find result object
  function getSelectedEmployeeWithDelegate(id: number): EmployeeWithDelegates {
    return employees.find(
      (user) => user.employeeId === id
    ) as EmployeeWithDelegates;
  }

  const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
    const { children, classes, onClose, ...other } = props;
    return (
      <MuiDialogTitle disableTypography className={classes.root} {...other}>
        <Typography variant="h6">{children}</Typography>
        {onClose ? (
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        ) : null}
      </MuiDialogTitle>
    );
  });

  // modal that appears when a table row is clicked; different from
  // the add modal
  let modalContent = null;
  if (!!selectedEmployeeId) {
    modalContent = (
      <EmployeeWithDelegatesDetails
        data={getSelectedEmployeeWithDelegate(selectedEmployeeId)}
        isOpen={openModal}
        onClickClose={handleCloseModal}
      />
    );
  }

  // everything below this line will be for autofill
  function getDirectoryOptionsEmployeeEmail(): string[] {
    return directory_userEmails
      ? directory_userEmails.map((user) => user.email)
      : [];
  }

  function getDirectoryOptionsDelegateEmail(): string[] {
    return directory_delegateEmails
      ? directory_delegateEmails.map((user) => user.email)
      : [];
  }

  function handleEmployeeEmailInput(name: string, value: string): void {
    addForm.set(Form.target.VALUE, ["userEmail", value]);
  }

  function handleDelegateEmailInput(name: string, value: string): void {
    addForm.set(Form.target.VALUE, ["delegateEmail", value]);
  }

  function handleExport() {
    exportEmployeeDelegates();
  }

  const pageName = "Employees With Delegates";
  return (
    <Box mt={2}>
      {modalContent}
      <Dialog open={openAddForm} fullWidth>
        <DialogTitle id="form-dialog-title" onClose={closeAddForm}>
          Add Employee With Delegate
        </DialogTitle>
        <DialogContent>
          <Form>
            <Form.Autocomplete
              label="Employee Email"
              name="userEmail"
              size={12}
              value={addForm.values.userEmail}
              error={addForm.errors.userEmail}
              required={true}
              onChange={handleAddFormChange}
              onInput={handleEmployeeEmailInput}
              disabled={isSubmitting}
              options={getDirectoryOptionsEmployeeEmail()}
              loading={directory_userEmails_fetching}
            />
            <Form.Autocomplete
              label="Delegate Email"
              name="delegateEmail"
              size={12}
              value={addForm.values.delegateEmail}
              error={addForm.errors.delegateEmail}
              required={true}
              onChange={handleAddFormChange}
              onInput={handleDelegateEmailInput}
              disabled={isSubmitting}
              options={getDirectoryOptionsDelegateEmail()}
              loading={directory_delegateEmails_fetching}
            />
            <Form.Checkbox
              label="Delegate is active"
              name="isActive"
              size={12}
              value={addForm.values.isActive}
              onChange={handleAddFormChange}
              disabled={isSubmitting}
            />
          </Form>
        </DialogContent>
        <DialogActions>
          {isSubmitting ? (
            <CircularProgress size={20} />
          ) : (
            <Button onClick={handleAddFormSubmit} color="primary">
              Submit
            </Button>
          )}
          <Button
            onClick={closeAddForm}
            color="primary"
            disabled={isSubmitting}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <MaintenanceBreadcrumbs title={pageName} />
      <Button
        style={{ float: "right", marginBottom: "15px" }}
        onClick={handleExport}
      >
        EXPORT TO EXCEL
      </Button>
      <Grid container direction="row" spacing={1} justify="space-between">
        <Grid item xs={"auto"}>
          <Actions>
            <Actions.Action onClick={() => setOpenAddForm(true)}>
              + Add Employee W/ Delegate
            </Actions.Action>
          </Actions>
        </Grid>
        <Grid item xs={3}>
          <Search
            onSearchUpdate={onSearchUpdate}
            placeholder={"Search by Email or Name"}
          ></Search>
        </Grid>
      </Grid>
      <DataTable
        columns={columns}
        data={employees}
        highlightOnHover
        pagination
        paginationServer
        paginationTotalRows={total}
        paginationPerPage={perPage}
        paginationComponentOptions={{
          noRowsPerPage: true,
        }}
        pointerOnHover
        onChangePage={(page) => setPage(page)}
        paginationRowsPerPageOptions={[10, 30, 50]}
        onChangeRowsPerPage={(page) => setPage(page)}
        data-tag="allowRowEvents"
        onRowClicked={(row) => {
          setSelectedEmployeeId(row.employeeId);
          setOpenModal(true);
        }}
      />
    </Box>
  );
};
